import React from 'react';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import { UI_DATE_FORMAT } from 'globalconfig/constants/static';

/**
 * True when placeholder looks like a date-only pattern
 * (mm/dd/yyyy, MM/DD/YYYY, MM/YYYY, MM/DD/AAAA, etc.).
 */
export const isDateLikePlaceholder = (placeholderText = '') => {
  const ph = String(placeholderText || '').trim();
  if (!ph) return false;
  // Exclude obvious time-only placeholders
  if (/^\d{0,2}:?\d{0,2}\s*(am|pm)?$/i.test(ph) || /^h:mm/i.test(ph)) {
    return false;
  }
  // mm/dd/yyyy, mm-dd-yyyy, mm/yyyy, MM/DD/AAAA, etc.
  return /m{1,2}[\/\-\.](d{1,2}[\/\-\.])?(y{2,4}|aaaa)/i.test(ph);
};

/**
 * react-datepicker uses date-fns, which rejects moment tokens like DD/YYYY.
 * Convert common moment tokens → date-fns unicode tokens for display/input.
 * Keep the original moment format for parsing form values.
 */
export const momentToDateFnsFormat = (format = UI_DATE_FORMAT) =>
  String(format)
    .replace(/YYYY/g, 'yyyy')
    .replace(/YY/g, 'yy')
    .replace(/DD/g, 'dd')
    .replace(/D/g, 'd');

const shouldUseCalendarIcon = ({
  placeholderText,
  dateFormat,
  showTimeSelect,
  showTimeSelectOnly,
}) => {
  if (showTimeSelectOnly || showTimeSelect) return false;
  if (isDateLikePlaceholder(placeholderText)) return true;
  // No / non-date placeholder: fall back to dateFormat (default is date-only)
  const df = String(dateFormat || UI_DATE_FORMAT);
  const hasTimeToken = /H{1,2}|h{1,2}|s{1,2}|a/i.test(df);
  return !hasTimeToken;
};

/**
 * Wrapper around react-datepicker for use with Formik Field.
 * Derives `selected` (Date) from the form's string `value` so the calendar
 * opens showing the saved date instead of defaulting to today.
 * Uses UI_DATE_FORMAT (MM/DD/YYYY) for parsing; moment's mm/dd/yyyy would be
 * wrong (minutes/day-of-week/year). Pass dateFormat when value includes time.
 *
 * Adds wrapperClassName `gs-datepicker--date` or `gs-datepicker--time` so
 * styles can show calendar vs timepicker icons based on placeholder/format.
 */
const FormikDatePicker = ({
  value,
  dateFormat = UI_DATE_FORMAT,
  placeholderText,
  showTimeSelect,
  showTimeSelectOnly,
  wrapperClassName = '',
  ...rest
}) => {
  const selected = value
    ? (() => {
        const parsed = moment(value, dateFormat);
        return parsed.isValid() ? parsed.toDate() : null;
      })()
    : null;

  const useCalendar = shouldUseCalendarIcon({
    placeholderText,
    dateFormat,
    showTimeSelect,
    showTimeSelectOnly,
  });
  const iconClass = useCalendar ? 'gs-datepicker--date' : 'gs-datepicker--time';
  const mergedWrapperClass = [wrapperClassName, iconClass].filter(Boolean).join(' ');

  return (
    <ReactDatePicker
      {...rest}
      selected={selected}
      dateFormat={momentToDateFnsFormat(dateFormat)}
      placeholderText={placeholderText}
      showTimeSelect={showTimeSelect}
      showTimeSelectOnly={showTimeSelectOnly}
      wrapperClassName={mergedWrapperClass}
    />
  );
};

export default FormikDatePicker;
